import { iot, mqtt5 } from 'aws-iot-device-sdk-v2'
import { toUtf8 } from '@aws-sdk/util-utf8-browser'
import { PublishPacket, QoS, SubscribePacket } from 'aws-crt/dist/common/mqtt5_packet'

import { ResourceType, User } from '../types'
import { TelehealthCredentialProvider } from './telehealth-credential-provider'
import { CustomAuthoriserData } from '../services/service-utils'

export const enum ConnectionStatus {
  Connected = "Connected",
  Disconnected = "Disconnected",
  Reconnecting = "Reconnecting",
  Stopped = "Stopped"
}

export interface ResultPair {
  statusCode: number
  message: string
}

interface SubscriptionEntry {
  packet: SubscribePacket
  handleMessage: (topicName: string, payload: string) => void
}

export class IOTConnectionHandler {

  private tryGetStoredSession: () => Promise<string>
  private defaultMessageHandler: (topic: string, payload: string, userId: string) => void
  private region: string
  private websocketEndPoint: string
  private credentialProvider: TelehealthCredentialProvider
  private client: mqtt5.Mqtt5Client

  private userId: string | undefined = undefined
  private guest: boolean | undefined = undefined
  private clientIdSet: string[] | undefined = undefined

  private subDict: Record<string, SubscriptionEntry | undefined> = {}

  private _connectionStatus: ConnectionStatus = ConnectionStatus.Stopped
  private connectionStatusListeners: Array<(status: ConnectionStatus) => void> = []

  public get status(): ConnectionStatus {
    return this._connectionStatus
  }

  private set connectionStatus(value: ConnectionStatus) {
    if (this._connectionStatus !== value) {
      this._connectionStatus = value
      this.connectionStatusListeners.forEach(listener => listener(value))
    }
  }

  public onConnectionStatusChange(listener: (status: ConnectionStatus) => void) {
    this.connectionStatusListeners.push(listener)
  }

  public removeConnectionStatusChangeListener(listener: (status: ConnectionStatus) => void) {
    this.connectionStatusListeners = this.connectionStatusListeners.filter(l => l !== listener)
  }

  private validConnectionReasonCodes = [mqtt5.SubackReasonCode.GrantedQoS0, mqtt5.SubackReasonCode.GrantedQoS1, mqtt5.SubackReasonCode.GrantedQoS2]

  private subscriptions: mqtt5.Subscription[] = []

  constructor(
    region: string,
    websocketEndPoint: string,
    tryGetStoredSession: () => Promise<string>,
    defaultMessageHandler: (topic: string, payload: string, userId: string) => void
  ) {
    this.region = region
    this.websocketEndPoint = websocketEndPoint
    this.tryGetStoredSession = tryGetStoredSession
    this.defaultMessageHandler = defaultMessageHandler

    this.credentialProvider = new TelehealthCredentialProvider(this.tryGetStoredSession)

    const config = iot.AwsIotMqtt5ClientConfigBuilder.newWebsocketMqttBuilderWithSigv4Auth(
      this.websocketEndPoint,
      {
        region: this.region,
        credentialsProvider: this.credentialProvider,
      }).build()

    this.client = new mqtt5.Mqtt5Client(config)

    this.addEventListeners()
  }

  private addEventListeners() {
    this.client.on('connectionSuccess', async () => {
      console.log('~~ivai - Connected to AWS IoT')

      this.subscriptions = this.createSubscriptions()

      const packets: mqtt5.SubscribePacket[] = this.subscriptions.map(
        item => {
          return {
            subscriptions: [item]
          }
        }
      )

      let successCount = 0
      for (let i = 0; i < packets.length; i++) {
        const response = await this.client.subscribe(packets[i])
        for (let code of response.reasonCodes) {
          if (code > 2) {
            console.warn("Got subscription reason code", code)
          } else {
            successCount++
          }
        }
      }

      console.log(`~~ivai - Subscribing to AWS IoT (${successCount} of ${packets.length})`)

      this.connectionStatus = ConnectionStatus.Connected
    })

    this.client.on('connectionFailure', (error) => {
      console.error('~~ivai - Failed to connect to AWS IoT', error)
      this.connectionStatus = ConnectionStatus.Disconnected
    })

    this.client.on('error', (error) => {
      console.error('~~ivai - Disconnected from AWS IoT', error)
      this.connectionStatus = ConnectionStatus.Disconnected
    })

    this.client.on('disconnection', (error) => {
      console.log('~~ivai - disconnection from AWS IoT', error)
      this.connectionStatus = ConnectionStatus.Reconnecting
    })

    this.client.on('stopped', (event) => {
      console.log('~~ivai - Stopped connection to AWS IoT', event)
      this.connectionStatus = ConnectionStatus.Stopped
      this.client.close()
    })

    this.client.on("messageReceived", this.onEvent)

    this.client.on('attemptingConnect', () => {
      console.log('~~ivai - Attempting Connect')
    })
  }

  private createSubscriptions = (): mqtt5.Subscription[] => {
    if (this.guest) {
      const meetingId = this.credentialProvider?.customAuthData?.meetingId

      if (!meetingId) {
        console.error("~~ivai - is connected to iot as a guest but has no meetingId!")
        return []
      }

      return [{
        topicFilter: `update/${ResourceType.Chime}/${meetingId}/guestUpdate`,
        qos: mqtt5.QoS.AtLeastOnce
      }]
    }

    const basicSubscriptionSet = [
      ResourceType.Patient,
      ResourceType.Organisation,
      ResourceType.User,
      ResourceType.Meeting,
    ]

    const subscriptions: mqtt5.Subscription[] = []

    for (let type of basicSubscriptionSet) {
      for (let clientId of this.clientIdSet || []) {
        subscriptions.push({
          topicFilter: `update/${type}/${clientId}`,
          qos: mqtt5.QoS.AtLeastOnce
        })
      }
    }

    subscriptions.push({
      topicFilter: `update/${ResourceType.Client}`,
      qos: mqtt5.QoS.AtLeastOnce,
    })

    return subscriptions
  }

  private getUnsubscribePackets = () => {
    return this.subscriptions.map(type => type.topicFilter)
  }

  onEvent: mqtt5.MessageReceivedEventListener = (eventData: mqtt5.MessageReceivedEvent) => {

    if (!eventData.message.payload) {
      console.warn('No payload', eventData)
      return
    }

    if (!this.userId) {
      console.log('~~No current user to handle IoT message')
      return
    }

    const payload = toUtf8(eventData.message.payload as Buffer)

    const topicName = eventData.message.topicName

    if (this.subDict[topicName]) {
      this.subDict[topicName]?.handleMessage(topicName, payload)
      return
    }

    this.defaultMessageHandler(topicName, payload, this.userId)
  }

  async checkUserChange(
    currentUser: User | null,
    clientIdSet: string[],
    authData?: CustomAuthoriserData
  ) {

    if (!currentUser && !authData?.joinCode) {
      this.closeConnection('Disconnecting from AWS IoT due to unauthenticated user')
      return
    }

    const userId = currentUser ? currentUser.userId : authData?.userId
    const guest = !currentUser

    if (userId === this.userId && this.guest === guest) {
      return
    }

    await this.closeConnection('Disconnecting from AWS IoT due to user change')

    this.userId = userId
    this.guest = guest
    this.clientIdSet = clientIdSet

    if (authData?.joinCode || currentUser) {
      this.waitToConnect(authData)
    }
  }

  async waitToConnect(
    authData?: CustomAuthoriserData
  ) {

    if (!await this.credentialProvider.assumeIdentity(authData)) {
      return false
    }

    this.client.start()
  }

  async subscribeToChannel(topicName: string, handleMessage: (topicName: string, payload: string) => void): Promise<ResultPair> {
    if (!this.client) {
      console.warn('No connection to subscribe to')
      return {
        statusCode: 500,
        message: 'No connection to subscribe to'
      }
    }

    const subscription = {
      topicFilter: topicName,
      qos: mqtt5.QoS.AtLeastOnce,
    }

    const packet: SubscribePacket = {
      subscriptions: [
        subscription,
      ]
    }

    this.subscriptions.push(subscription)

    const subscriptionEntry: SubscriptionEntry = {
      packet,
      handleMessage
    }

    this.subDict[topicName] = subscriptionEntry

    // create a timeout to handle the case where the subscription fails
    const response = await this.client.subscribe(packet)

    if (!this.validConnectionReasonCodes.includes(response.reasonCodes[0])) {
      console.error('~~Subscription failed', response)

      return {
        statusCode: 500,
        message: response.reasonString || 'Subscription failed'
      }
    }

    return {
      statusCode: 200,
      message: 'Subscription successful'
    }
  }

  sendMessage(topicName: string, payload: string) {
    if (!this.client) {
      console.warn('No connection to send message')
      return
    }

    const packet: PublishPacket = {
      topicName,
      qos: QoS.AtLeastOnce,
      payload,
    }

    this.client.publish(packet)
  }

  unsubscribeFromChannel = async (topicName: string) => {

    if (!this.client) {
      console.error("Tried to unsubscribe when there was no client")
      return
    }

    const packet: mqtt5.UnsubscribePacket = {
      topicFilters: [topicName]
    }

    await this.client.unsubscribe(packet)

    this.subscriptions = this.subscriptions.filter(item => item.topicFilter === topicName)
    this.subDict[topicName] = undefined

    console.log('~~Unsubscribed from', topicName)
  }

  async closeConnection(message?: string) {
    if (this._connectionStatus == ConnectionStatus.Stopped) {
      return
    }

    const unSubPacket: mqtt5.UnsubscribePacket = {
      topicFilters: this.getUnsubscribePackets(),
    }

    if (unSubPacket.topicFilters.length > 0) {
      await this.client.unsubscribe(unSubPacket)
    }

    this.userId = undefined
    this.subscriptions = []
    this.client.stop()

    if (message) {
      console.log(message)
    }
  }
}